import { del, get, post, put } from '../request';
import { toPage, type Page, type PageQuery } from './types';
import type { Cdr } from './cdr';

export interface Customer {
  id: number;
  name: string;
  phone: string;
  gender?: string;
  company?: string;
  tags?: string; // 逗号拼接字符串
  remark?: string;
  last_call_at?: string;
  created_at?: string;
  [k: string]: any;
}

/** 表单态：tags 为 InputTag 数组 */
export interface CustomerForm {
  name: string;
  phone: string;
  gender?: string;
  company?: string;
  tags: string[];
  remark?: string;
  /** v5 客户自定义字段值 {field_key: value} */
  field_values?: Record<string, string>;
}

export interface TimelineItem {
  type: 'call' | 'chat' | 'ticket';
  id: number;
  time: string;
  title: string;
  subtitle: string;
  direction?: string;
  peer?: string;
  status?: string;
  agent_name?: string;
  duration?: number;
  /** 该次接触的备注原文（通话=服务小结 note；无则空） */
  note?: string;
  /** 该次接触的关键词（带类型供语义配色）：t∈category|result|tag|intent|emotion|content，v=文案；无则空 */
  keywords?: { t: string; v: string }[];
}

/** GET /customers/:id/timeline。silent：调用方按空态降级时用（如工作台通话语境，客户不在本坐席
 *  数据范围会 404，属正常态，不该弹错——见 server/internal/module/crm/scope.go 的可见性口径）。 */
export async function getTimeline(id: number, limit = 50, opts?: { silent?: boolean }): Promise<TimelineItem[]> {
  const res = await get(`/customers/${id}/timeline`, { limit }, opts);
  return (res as any)?.items || [];
}

export interface CustomerQuery extends PageQuery {
  keyword?: string;
  phone?: string; // 精确匹配（来电弹屏客户匹配用）
}

/** GET /customers */
export async function listCustomers(q: CustomerQuery): Promise<Page<Customer>> {
  return toPage<Customer>(await get('/customers', q));
}

export interface CustomerStats {
  total: number;
  month_new: number;
  active_30d: number;
  high_value_90d: number;
}

/** GET /customers/stats — 客户总览统计（总数/本月新增/活跃30天/高价值近90天互动≥3） */
export async function getCustomerStats(): Promise<CustomerStats> {
  return get('/customers/stats') as Promise<CustomerStats>;
}

/** GET /customers/:id */
export async function getCustomer(id: number): Promise<Customer> {
  return get(`/customers/${id}`);
}

/** 通话上下文客户匹配（t6 §6C1，GET /customers/by-call?uuid=&phone=）：
 * 服务端按身份核验通过号（KeyCallVerifiedPhone → call:active.verified_phone）优先匹配，
 * 未核验/核验号缺失时回落对端号 phone；uuid 归属校验在服务端。明文仅经坐席 REST 返回（WS 恒脱敏）。 */
export interface CallCustomerMatch {
  customer: Customer | null;
  /** 实际参与匹配的号码（核验号或回落号，已归一化） */
  phone?: string;
  /** true=按后端已核验号码匹配（核验号≠对端号场景） */
  verified?: boolean;
}

/** GET /customers/by-call — 语音通话语境客户匹配（静默失败：归属校验拒绝/异常由调用方回落既有反查） */
export async function getCustomerByCall(p: { uuid?: string; phone?: string }): Promise<CallCustomerMatch> {
  const params: Record<string, string> = {};
  if (p.uuid) params.uuid = p.uuid;
  if (p.phone) params.phone = p.phone;
  return get('/customers/by-call', params, { silent: true });
}

/** tags 逗号字符串 → 数组 */
export function splitTags(tags?: string | string[]): string[] {
  if (Array.isArray(tags)) return tags;
  return (tags || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** 读取：tags → 数组 */
export function customerToForm(c: Customer): CustomerForm {
  return {
    name: c.name || '',
    phone: c.phone || '',
    gender: c.gender || 'unknown',
    company: c.company || '',
    tags: splitTags(c.tags),
    remark: c.remark || '',
  };
}

/** 提交：tags 数组 → 逗号拼接字符串；field_values 透传（v5） */
function toPayload(f: Partial<CustomerForm>): Record<string, any> {
  const p: Record<string, any> = {};
  if (f.name !== undefined) p.name = f.name;
  if (f.phone !== undefined) p.phone = f.phone;
  if (f.gender !== undefined) p.gender = f.gender;
  if (f.company !== undefined) p.company = f.company;
  if (f.remark !== undefined) p.remark = f.remark;
  if (f.tags !== undefined) p.tags = f.tags.join(',');
  if (f.field_values !== undefined) p.field_values = f.field_values;
  return p;
}

// ---------- v5 客户自定义字段目录 ----------

export interface CustomerField {
  id: number;
  field_key: string;
  label: string;
  type?: string;
  options?: string;
  required?: boolean;
  enabled?: boolean;
  sort?: number;
  created_at?: string;
}

export async function listCustomerFields(): Promise<CustomerField[]> {
  const res = await get('/customer-fields');
  return Array.isArray(res) ? (res as CustomerField[]) : [];
}

export function createCustomerField(p: Partial<CustomerField>) {
  return post('/customer-fields', p);
}

export function updateCustomerField(id: number, p: Partial<CustomerField>) {
  return put(`/customer-fields/${id}`, p);
}

export function deleteCustomerField(id: number) {
  return del(`/customer-fields/${id}`);
}

export function createCustomer(f: CustomerForm) {
  return post('/customers', toPayload(f));
}

export function updateCustomer(id: number, f: Partial<CustomerForm>) {
  return put(`/customers/${id}`, toPayload(f));
}

export function deleteCustomer(id: number) {
  return del(`/customers/${id}`);
}

/** GET /customers/:id/cdrs */
export async function listCustomerCdrs(id: number, q?: PageQuery): Promise<Page<Cdr>> {
  return toPage<Cdr>(await get(`/customers/${id}/cdrs`, { page: 1, pageSize: 50, ...(q || {}) }));
}
